import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import _isFunction from 'lodash/isFunction'
import _kebabCase from 'lodash/kebabCase'
import _get from 'lodash/get'
import { Remarkable } from 'remarkable'
import _replace from 'lodash/replace'
import {
  PLAN_SKU_SAGE_SPECIAL_ACCESS,
  PLAN_SKU_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY,
  PLAN_SKU_ONE_MONTH_TRIAL_TO_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  isFourteenMonthPlan,
  PLAN_SUBSCRIPTION_LIVE,
} from '../../services/plans/index'
import {
  EUR,
  CAD,
  renderSymbol as renderCurrencySymbol,
} from '../../services/currency/index'
import { FR } from '../../services/languages/constants'
import { scrollToTop } from '../../services/dom/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import Icon, { ICON_TYPES } from '../Icon.v2/index'
import { SLIDER_INACTIVE_INDEX } from '../Slider/index'
import PlanValueProp from '../PlanValueProp/index'
import { get as getConfig } from '../../config/index'
import OptimizelyFeature from '../OptimizelyFeature/index'

const config = getConfig()
const fullPriceFirstEnabled = !!_get(config, ['features', 'checkout', 'planFullPriceFirst'])

const md = new Remarkable({
  inline: true,
  breaks: true,
  html: false,
})
// Override the paragraph renderer to not wrap content in p tags
md.renderer.rules.paragraph_open = () => ''
md.renderer.rules.paragraph_close = () => ''

// Single Page Checkout - slider
const SLIDER_MOD_MAX_PROPS = 4
const SLIDER_GAIA_PLUS_INDEX = 2

function PlanCardValueProps (props) {
  const {
    valueProps,
    sliderMode,
    isGaiaPlus,
    activeSlide,
    staticText,
    setActiveSlide,
  } = props
  const [isPropsHidden, setIsPropsHidden] = useState(true)
  const hiddenPropsCount = valueProps.size - SLIDER_MOD_MAX_PROPS

  const renderProp = (index, prop, shouldHide) => (
    <div className={`plan-card__value-prop${shouldHide ? ' plan-card__value-prop--hidden' : ''}`} key={index}>
      <Icon className="plan-card__value-prop-check" type={ICON_TYPES.CHECK} />
      <PlanValueProp valueProp={prop} className="plan-card__value-prop-text" />
    </div>
  )

  const handleViewMoreClick = () => {
    setIsPropsHidden(false)
    setActiveSlide(SLIDER_GAIA_PLUS_INDEX)
  }

  useEffect(() => {
    if (activeSlide !== SLIDER_GAIA_PLUS_INDEX) {
      setIsPropsHidden(true)
    }
    if (activeSlide === SLIDER_INACTIVE_INDEX || activeSlide === SLIDER_GAIA_PLUS_INDEX) {
      setIsPropsHidden(false)
    }
  }, [activeSlide])

  return (
    <div className="plan-card__value-props">
      {
        valueProps && List.isList(valueProps)
          ? valueProps.map((prop, index) => {
            const shouldHide = sliderMode
              && isGaiaPlus
              && index >= SLIDER_MOD_MAX_PROPS
              && isPropsHidden
            return renderProp(index, prop, shouldHide)
          })
          : null
      }
      {
        sliderMode && isGaiaPlus && isPropsHidden && (
          <Button
            className="plan-card__value-props-view-more"
            onClick={handleViewMoreClick}
            type={BUTTON_TYPES.LINK}
          >
          {
            // eslint-disable-next-line no-template-curly-in-string
            _replace(staticText.get('viewMore', ''), '${ hiddenPropsCount }', hiddenPropsCount)
          }
          </Button>
        )
      }
    </div>
  )
}

function PlanCard (props) {
  const {
    auth,
    plan = Map(),
    staticText = Map(),
    planClick,
    isAccountChangePlan,
    shouldScrollToTop,
    sliderMode,
    activeSlide,
    setActiveSlide,
    index,
    userLanguage,
    userPlanIsExactMatch,
    userNextPlanIsExactMatch,
    plans,
    cancellationScheduled,
    sendOptimizelyFeatureEvent,
    optimizelyFeature = Map(),
    onPopUpMouseEnter,
    isPopUpElevated,
    centerPopUp,
    touchable,
  } = props

  // ANON-1757 pop up experiment
  const isPopUpMode = _isFunction(onPopUpMouseEnter)
  const planType = plan.get('type')
  const title = plan.get('title')
  const billed = plan.get('billed')
  const billedAbb = plan.get('billedAbb')
  const feature = plan.get('feature')
  const promoValue1 = plan.get('promoValue1')
  const promoValue2 = plan.get('promoValue2')
  const hasPromoValue = promoValue1 || promoValue2
  const showStandardFeatureBlock = feature && !hasPromoValue && !isAccountChangePlan
  const showPromoFeatureBlock = hasPromoValue && !isAccountChangePlan
  const showEmptyFeaturePlaceholder = !showStandardFeatureBlock && !showPromoFeatureBlock
  const currencyIso = plan.get('currencyIso')
  const cost = plan.get('cost')
  const price = plan.get('price')
  const initialCost = plan.get('initialCost')
  const valueProps = plan.get('valueProps')
  const freeTrial = plan.get('freeTrial')
  const bestValue = plan.get('bestValue')
  const percentSavings = plan.get('percentSavings')
  // AP-16214 Black Friday Checkout - 2024
  const isFourteenMonth = isFourteenMonthPlan(plan.get('sku'))
  const showTrialPriceAsMainPrice = plan.get('sku') === PLAN_SKU_SAGE_SPECIAL_ACCESS
    || plan.get('sku') === PLAN_SKU_ONE_MONTH_TRIAL_TO_ANNUAL
    || plan.get('sku') === PLAN_SKU_ONE_DOLLAR_THIRTY_DAYS_TO_MONTHLY
  const bestValueCopy = isFourteenMonth ? staticText.get('blackFridayBestValue') : staticText.get('bestValue')
  const isMonthly = planType === PLAN_SUBSCRIPTION_MONTHLY
  const monthlyCostUnformatted = plan.get('monthlyCostUnformatted')
  const weeklyCostUnformatted = plan.get('weeklyCostUnformatted')
  const planAlreadySelected = isAccountChangePlan
    && (userPlanIsExactMatch || userNextPlanIsExactMatch)
    && !cancellationScheduled

  // ANON-1746 best value experiment
  const isBestValueExperimentEnabled = optimizelyFeature.getIn(['flags', 'checkout__plan_best_value', 'variation']) === 'save_percent'

  // ANON-1757 pop up experiment
  const handlePopUpMouseEnter = () => {
    if (!isPopUpMode || touchable) {
      return
    }
    onPopUpMouseEnter()
  }

  // Single Page Checkout - slider
  const selectedPlan = plans.get('selection') || null
  const isCurrentPlanSelected = selectedPlan
    && selectedPlan.get('sku') === plan.get('sku')
    && !!auth.get('jwt') // disable if the account is not created
  const isGaiaPlusPlan = planType === PLAN_SUBSCRIPTION_LIVE

  const scrollToTopOnClick = () => {
    return new Promise((resolve) => {
      if (window.scrollY === 0) {
        resolve()
        // return so we don't get a listener set if the user doesn't scroll
        return
      }

      const onScroll = () => {
        if (window.scrollY === 0) {
          window.removeEventListener('scroll', onScroll)
          resolve()
        }
      }

      window.addEventListener('scroll', onScroll)
      scrollToTop()
    })
  }

  const onPlanSelectClicked = () => {
    if (!sliderMode && !planAlreadySelected) {
      sendOptimizelyFeatureEvent({
        eventName: 'plan_select_cta_click',
        tags: {
          SKU: plan.get('sku'),
          plan_type: plan.get('type'),
        },
      })
    }
    if (shouldScrollToTop) {
      return scrollToTopOnClick().then(() => {
        planClick()
      })
    }
    // Single Page Checkout - slider
    if (sliderMode && activeSlide !== SLIDER_INACTIVE_INDEX) {
      setActiveSlide(index)
    }
    return planClick()
  }

  const renderPrice = (planPrice, abbreviationFromData, abbreviationText, showCents = true) => {
    if (!planPrice) {
      return null
    }

    const [roundCost, centsCost = '00'] = planPrice.split('.')
    const showCentsPrice = (showCents || parseInt(centsCost, 10) > 0)
    const showEurStylePrice = currencyIso === EUR || (currencyIso === CAD && userLanguage === FR)

    return (
      <>
        {`${!showEurStylePrice ? renderCurrencySymbol(currencyIso) : ''}${roundCost}${showEurStylePrice && !showCentsPrice ? ` ${renderCurrencySymbol(currencyIso)}` : ''}`}
        {showCentsPrice && <span className="plan-card__cents">{`${showEurStylePrice ? ',' : '.'}${centsCost}${showEurStylePrice ? ` ${renderCurrencySymbol(currencyIso)}` : ''}`}</span>}
        {abbreviationFromData && <span className="plan-card__abbreviation">/{abbreviationText}</span>}
      </>
    )
  }

  const getWrapperClassName = () => {
    const baseClassName = 'plan-card'
    const classNames = [baseClassName]

    if (bestValue && !fullPriceFirstEnabled) {
      classNames.push(`${baseClassName}--first`)
    }

    if (bestValue && isBestValueExperimentEnabled) {
      classNames.push(`${baseClassName}--save-percent`)
    }

    if (planAlreadySelected) {
      classNames.push(`${baseClassName}--current`)
    }

    // Single Page Checkout - slider
    if (sliderMode) {
      classNames.push(`${baseClassName}--slide`)
      if (activeSlide === SLIDER_GAIA_PLUS_INDEX) classNames.push(`${baseClassName}--expanded`)
    }

    // ANON-1757 pop up experiment
    if (isPopUpMode && isPopUpElevated) {
      classNames.push(`${baseClassName}--pop-up`)
    }

    if (isPopUpMode && !isPopUpElevated) {
      classNames.push(`${baseClassName}--pop-down`)

      if (centerPopUp) {
        classNames.push(`${baseClassName}--pop-down-centered`)
      }
    }

    return classNames.join(' ')
  }

  const getButtonText = () => {
    if (userPlanIsExactMatch) {
      return staticText.get('currentPlan')
    }

    if (userNextPlanIsExactMatch) {
      return staticText.get('nextPlan')
    }

    return staticText.get('select')
  }

  // Show Plan Full Price First (fullPriceFirstEnabled)
  const fullPrice = renderPrice(
    showTrialPriceAsMainPrice ? initialCost : cost,
    billedAbb,
    billedAbb,
    false,
  )
  const monthlyPrice = renderPrice(
    showTrialPriceAsMainPrice ? initialCost : monthlyCostUnformatted,
    billedAbb,
    staticText.get('monthAbbreviation'),
    !showTrialPriceAsMainPrice,
  )
  const weeklyPrice = renderPrice(
    weeklyCostUnformatted,
    true,
    staticText.get('weekAbbreviation'),
    !showTrialPriceAsMainPrice,
  )

  const renderPlanSecondaryLine = () => {
    return (
      <>
        <OptimizelyFeature
          flag="checkout__plan_weekly_pricing"
          variation="control"
          isDefault
          anon={false}
          noWrapper
        >
          {!isMonthly && bottomPrice}
        </OptimizelyFeature>
        <OptimizelyFeature
          flag="checkout__plan_weekly_pricing"
          variation="show_weekly"
          isDefault={false}
          anon={false}
          noWrapper
        >
          {weeklyPrice}
        </OptimizelyFeature>
      </>
    )
  }
  // Prices according to position - fullPriceFirst reverses the order
  const topPrice = fullPriceFirstEnabled ? fullPrice : monthlyPrice
  const bottomPrice = fullPriceFirstEnabled ? monthlyPrice : fullPrice

  const bestValueBadgeClassName = `plan-card__best-value${isFourteenMonth ? ' plan-card__best-value--black-friday' : ''}`
  // eslint-disable-next-line no-template-curly-in-string
  const percentSavingPlaceholder = '${percentSaving}'
  const bestValueSavePercentText = (percentSavings != null && percentSavings > 0)
    ? _replace(staticText.get('bestValueSavePercent'), percentSavingPlaceholder, String(percentSavings))
    : ''

  const renderBestValueBadge = () => {
    if (!bestValue) {
      return null
    }
    if (isFourteenMonth) {
      return (
        <div className={bestValueBadgeClassName}>
          {bestValueCopy}
        </div>
      )
    }
    return (
      <>
        <OptimizelyFeature
          flag="checkout__plan_best_value"
          variation="control"
          isDefault
          anon={false}
          noWrapper
        >
          <div className={`plan-card__best-value${isFourteenMonth ? ' plan-card__best-value--black-friday' : ''}`}>
            {bestValueCopy}
          </div>
        </OptimizelyFeature>
        <OptimizelyFeature
          flag="checkout__plan_best_value"
          variation="save_percent"
          isDefault={false}
          anon={false}
          noWrapper
        >
          <div className="plan-card__best-value plan-card__best-value--save-percent">
            {`${staticText.get('bestValue')} `}
            <span>
              {bestValueSavePercentText}
            </span>
          </div>
        </OptimizelyFeature>
      </>
    )
  }

  return (
    <div
      className={getWrapperClassName()}
      onMouseEnter={isPopUpMode && !touchable ? handlePopUpMouseEnter : undefined}
    >
      { renderBestValueBadge() }
      <div className="plan-card__header">
        <div className={`plan-card__header-top${planAlreadySelected ? ' plan-card__header-top--current' : ''}`}>
          <div className="plan-card__title">{title}</div>
          <div className="plan-card__monthly-cost">
            {topPrice}
          </div>
        </div>
        <div className={`plan-card__header-bottom${planAlreadySelected ? ' plan-card__header-bottom--current' : ''}`}>
          <div className="plan-card__billing-period">{billed || '\u00A0'}</div>
          <div className={`plan-card__price${fullPriceFirstEnabled ? ' plan-card__price--full-price-first' : ''}`}>
            {renderPlanSecondaryLine()}
          </div>
        </div>
      </div>
      {
        // Single Page Checkout - slider
        sliderMode
          ? <Button
            className={`plan-card__select-button${isCurrentPlanSelected ? ' plan-card__select-button--selected' : ''}`}
            onClick={isCurrentPlanSelected ? null : onPlanSelectClicked}
            data-action={`plan-select-${_kebabCase(planType)}`}
            type={isCurrentPlanSelected ? BUTTON_TYPES.SECONDARY : BUTTON_TYPES.PRIMARY}
            disabled={isCurrentPlanSelected}
          >
            {isCurrentPlanSelected
              ? <>
                <Icon className="plan-card__select-button-check" type={ICON_TYPES.CHECK} />
                {staticText.get('selectedPlan')}
              </>
              : staticText.get('selectPlan')
            }
          </Button>
          : (
            <Button
              className="plan-card__select-button"
              onClick={planAlreadySelected ? null : onPlanSelectClicked}
              data-action={`plan-select-${_kebabCase(planType)}`}
              type={BUTTON_TYPES.PRIMARY}
              disabled={planAlreadySelected}
            >
              {getButtonText()}
            </Button>
          )
      }
      {showStandardFeatureBlock && (
        <div className="plan-card__feature">
          <div className={`plan-card__feature-title ${freeTrial ? 'plan-card__feature-title--teal' : ''}`}>
            {feature}
          </div>
          {!showTrialPriceAsMainPrice && (
            <div className="plan-card__feature-billing-info">
              {`${freeTrial || isFourteenMonth ? `${staticText.get('then')} ` : ''} ${price} ${billed}, ${staticText.get('cancelAnytime')}`}
            </div>
          )}
          {showTrialPriceAsMainPrice && (
            <div className="plan-card__feature-billing-info">
              {`${staticText.get('then')} ${price} ${staticText.get('billedMonthly')}, ${staticText.get('cancelAnytime')}`}
            </div>
          )}
        </div>
      )}
      {showEmptyFeaturePlaceholder && (
        <div className="plan-card__feature plan-card__feature--empty" />
      )}
      {showPromoFeatureBlock && (
        <div className="plan-card__feature">
          <div
            className={`plan-card__feature-title ${freeTrial ? 'plan-card__feature-title--teal' : ''}`}
            dangerouslySetInnerHTML={{ __html: md.render(promoValue1) }}
          />
          <div
            className="plan-card__feature-billing-info"
            dangerouslySetInnerHTML={{ __html: md.render(promoValue2) }}
          />
        </div>
      )}
      {
        // Single Page Checkout - slider
        valueProps && <PlanCardValueProps
          valueProps={valueProps}
          sliderMode={sliderMode}
          isGaiaPlus={isGaiaPlusPlan}
          activeSlide={activeSlide}
          staticText={staticText}
          setActiveSlide={setActiveSlide}
        />
      }
    </div>
  )
}

PlanCard.propTypes = {
  plan: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  planClick: PropTypes.func.isRequired,
  currentPlan: ImmutablePropTypes.map,
  useGraphQLData: PropTypes.bool,
  sliderMode: PropTypes.bool,
  isActiveSlide: PropTypes.bool,
  setActiveSlide: PropTypes.func,
  index: PropTypes.number,
  userLanguage: PropTypes.string,
  userPlanIsExactMatch: PropTypes.bool,
  userNextPlanIsExactMatch: PropTypes.bool,
  sendOptimizelyFeatureEvent: PropTypes.func,
  isPopUpElevated: PropTypes.bool,
  onPopUpMouseEnter: PropTypes.func,
  centerPopUp: PropTypes.bool,
  touchable: PropTypes.bool,
}

PlanCard.defaultProps = {
  sendOptimizelyFeatureEvent: () => {},
  isPopUpElevated: false,
  centerPopUp: false,
  touchable: false,
}

export default PlanCard
